import type { ActionConfig, ActionContext, ActionUser, TableName } from './types';
import { clientActions } from './clients';
import { employeeActions } from './employees';
import { officeNoteActions } from './office-notes';
import { qaReportActions } from './qa-reports';
import { requestActions } from './requests';
import { scheduleActions } from './schedules';
import { serviceLogActions } from './service-logs';
import { workorderActions } from './workorders';

// =============================================================================
// ACTION REGISTRY
// =============================================================================

const actionsByTable: Record<TableName, ActionConfig[]> = {
  workorders: workorderActions,
  clients: clientActions,
  employees: employeeActions,
  serviceLogs: serviceLogActions,
  schedules: scheduleActions,
  officeNotes: officeNoteActions,
  qaReports: qaReportActions,
  requests: requestActions,
};

// =============================================================================
// LOOKUP HELPERS
// =============================================================================

/**
 * Get all actions registered for a table
 */
export function getActionsForTable(table: TableName): ActionConfig[] {
  return actionsByTable[table] || [];
}

/**
 * Find a specific action by its ID within a table
 */
export function getActionById(table: TableName, id: string): ActionConfig | undefined {
  return actionsByTable[table]?.find((a) => a.id === id);
}

/**
 * Check if an action should be visible based on user permissions and row data
 */
export function isActionVisible(
  action: ActionConfig,
  row: Record<string, unknown>,
  user: ActionUser
): boolean {
  // Check view tag permissions
  if (action.requiredViews?.length) {
    const hasPermission = action.requiredViews.some((tag) => user.viewTags.includes(tag));
    if (!hasPermission) return false;
  }

  // Check row-based condition
  if (action.condition) {
    if (!action.condition(row, user)) return false;
  }

  return true;
}

/**
 * Get all visible actions for a given context (list, card, or detail view)
 */
export function getVisibleActions(
  table: TableName,
  row: Record<string, unknown>,
  user: ActionUser,
  context: ActionContext
): ActionConfig[] {
  const allActions = getActionsForTable(table);

  // Filter out hidden actions and check visibility
  const visible = allActions.filter(
    (action) => action.prominence !== 'hidden' && isActionVisible(action, row, user)
  );

  // Filter by context - different views show different prominence levels
  const contextFiltered = visible.filter((action) => {
    // Respect explicit context exclusions
    if (action.excludeContexts?.includes(context)) return false;

    switch (context) {
      case 'list':
        // Lists only show prominent actions (primary CTA)
        return action.prominence === 'prominent';
      case 'card':
        // Cards show prominent and inline actions
        return action.prominence === 'prominent' || action.prominence === 'inline';
      case 'detail':
        // Detail views show all non-hidden actions
        return true;
    }
  });

  // Sort by order (lower = higher priority)
  return contextFiltered.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}

// =============================================================================
// CONVENIENCE HELPERS FOR SPECIFIC PROMINENCE LEVELS
// =============================================================================

/**
 * Get prominent actions (primary CTAs, shown as buttons)
 */
export function getProminentActions(
  table: TableName,
  row: Record<string, unknown>,
  user: ActionUser
): ActionConfig[] {
  return getVisibleActions(table, row, user, 'detail').filter(
    (a) => a.prominence === 'prominent'
  );
}

/**
 * Get inline actions (shown directly in UI, e.g., navigation links)
 */
export function getInlineActions(
  table: TableName,
  row: Record<string, unknown>,
  user: ActionUser
): ActionConfig[] {
  return getVisibleActions(table, row, user, 'detail').filter(
    (a) => a.prominence === 'inline'
  );
}

/**
 * Get menu actions (shown in overflow/dropdown menus)
 */
export function getMenuActions(
  table: TableName,
  row: Record<string, unknown>,
  user: ActionUser
): ActionConfig[] {
  return getVisibleActions(table, row, user, 'detail').filter(
    (a) => a.prominence === 'menu'
  );
}

// Re-export types for convenience
export * from './types';
